import { useMemo } from 'react';

import { mergeFilters, resolveRequestSource } from '@/modules/analytics/constants/metricsCatalog';
import { DEFAULT_RANGE, resolveRange } from '@/modules/analytics/constants/timeRange';
import { useMultiGroupLabelResolver } from '@/modules/analytics/hooks/useGroupLabelResolver';
import {
  buildValueFormatter,
  computeDerivedValue,
  formatGroupKey,
  toNumber,
  useReportMetric,
} from '@/modules/analytics/hooks/useReportMetric';
import type { MetricsQueryRequest } from '@/shared/types/metricsQuery';
import type { ReportMetric } from '@/shared/types/reports';

export type ReportMetricTableRow = {
  rowKey: string;
  groupValues: Record<string, { key: string; label: string }>;
  value: number;
};

const ROW_KEY_SEPARATOR = '__';

const buildTableRequest = (metric: ReportMetric): MetricsQueryRequest | null => {
  if ((metric.query.groupBy ?? []).length === 0) {
    return null;
  }
  const { timeRange } = resolveRange(metric.range ?? DEFAULT_RANGE);
  // See buildSeriesRequest in useReportMetric.ts — same healing (re-pointed
  // table/aggregations, no-longer-groupable columns stripped) and the same
  // identity-filter merge, so chart and table stay aligned.
  const source = resolveRequestSource(metric.query, metric.metricId);
  const groupBy = source.groupBy ?? [];
  if (groupBy.length === 0) {
    return null;
  }
  const filter = mergeFilters(source.metric?.defaultFilter, metric.query.filter);
  return {
    table: source.table,
    ...(filter ? { filter } : {}),
    aggregations: source.aggregations,
    groupBy,
    timeRange,
  };
};

export const useReportMetricTable = (metric: ReportMetric | null | undefined) => {
  const request = useMemo(() => (metric ? buildTableRequest(metric) : null), [metric]);
  const query = useReportMetric(request);
  // Group and read rows by the healed request's columns, not the persisted
  // snapshot's — a stripped (no-longer-groupable) column won't be in the
  // response, and after a heal the value alias is the catalog's.
  const groupColumns = useMemo(() => request?.groupBy ?? [], [request]);
  const resolveLabel = useMultiGroupLabelResolver(groupColumns, query.data?.data);
  const derived = useMemo(
    () =>
      metric ? resolveRequestSource(metric.query, metric.metricId).metric?.derived : undefined,
    [metric]
  );
  const valueFormatter = useMemo(() => buildValueFormatter(derived), [derived]);

  const rows = useMemo<ReportMetricTableRow[]>(() => {
    if (!metric || !query.data || groupColumns.length === 0) {
      return [];
    }
    const valueAlias = request?.aggregations?.[0]?.alias ?? 'value';
    return query.data.data.flatMap((row) => {
      const value = derived ? computeDerivedValue(row, derived) : toNumber(row[valueAlias]);
      if (value == null) {
        return [];
      }
      const groupValues: Record<string, { key: string; label: string }> = {};
      for (const column of groupColumns) {
        const key = formatGroupKey(row[column]);
        groupValues[column] = { key, label: resolveLabel(column, key) };
      }
      const rowKey = groupColumns.map((c) => groupValues[c].key).join(ROW_KEY_SEPARATOR);
      return [{ rowKey, groupValues, value }];
    });
  }, [metric, request, query.data, groupColumns, resolveLabel, derived]);

  return { ...query, rows, groupColumns, valueFormatter };
};
